/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module modals/EventCancellationModal/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../../utils/logger.js";
import { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { refreshToken, signOut } from "../../utils/common.js";

import styles from "./index.module.css";

const EventCancellationModal = ({ open, event, onCancel, onSuccess }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dialogRef = useRef(null);
  const [attendeesNumber, setAttendeesNumber] = useState(null);

  useEffect(() => {
    const handleClose = () => {
      onCancel();
    };

    const dialog = dialogRef.current;
    if (dialog) {
      dialog.addEventListener("close", handleClose);
    }

    if (open) {
      getEventAttendees();
      dialog.showModal();
    } else {
      dialog.close();
    }

    return () => {
      if (dialog) {
        dialog.removeEventListener("close", handleClose);
      }
    };
  }, [open, onCancel]);

  const getEventAttendees = async () => {
    const options = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    };

    try {
      const response = await fetch(
        `${process.env.REACT_APP_ROOT_API}attendee/event/${event._id}`,
        options,
      );
      const data = await response.json();
      logger.info("getEventAttendees -> data", { data: data });
      if (data?.message === "The incoming token has expired") {
        const newTokens = await refreshToken();
        if (!newTokens) {
          await signOut();
          navigate("/");
          return;
        }
        await getEventAttendees();
        return;
      }
      const currentAttendees = Array.isArray(data) ? data : [];
      setAttendeesNumber(currentAttendees.length);
    } catch (error) {
      logger.error("Error", { err: sanitizeError(error) });
      setAttendeesNumber(0);
    }
  };

  const cancelEvent = async () => {
    const options = {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    };

    try {
      const response = await fetch(
        `${process.env.REACT_APP_ROOT_API}event/${event._id}/cancel`,
        options,
      );
      const data = await response.json();
      logger.info("cancelEvent -> data", { data: data });
      if (data?.message === "The incoming token has expired") {
        const newTokens = await refreshToken();
        if (!newTokens) {
          await signOut();
          navigate("/");
          return;
        }
        await cancelEvent();
        return;
      }
      onSuccess();
    } catch (error) {
      logger.error("Error", { err: sanitizeError(error) });
    }
  };

  return (
    <>
      {open && <div className={styles.overlay}></div>}
      <dialog ref={dialogRef} className={styles.modal}>
        <div className={styles.closeButtonContainer}>
          <button className={styles.closeButton} onClick={onCancel}>
            &times;
          </button>
        </div>

        <div className={styles.modalContent}>
          <label className={styles.modalTitle}>
            {t("components.cancelEventModal.header")}
          </label>
          <div className={styles.textContainer}>
            <label className={styles.modalSubtitle}>
              {t("components.cancelEventModal.subheader1")}
              {attendeesNumber}
              {t("components.cancelEventModal.subheader2")}
            </label>
            <label className={styles.messageText}>
              {t("components.cancelEventModal.message")}
            </label>
          </div>
        </div>

        <div className={styles.modalButtons}>
          <button onClick={onCancel} className={styles.cancelButton}>
            {t("components.cancelEventModal.cancel")}
          </button>
          <button className={styles.confirmButton} onClick={cancelEvent}>
            {t("components.cancelEventModal.accept")}
          </button>
        </div>
      </dialog>
    </>
  );
};

export default EventCancellationModal;
